<template>
  <div
    id="drill-list-view"
    class="pa-4"
  >
    <view-page-header
      v-if="attachMode === false"
      :show-team-select="false"
    />

    <div>
      <v-row
        no-gutters
        align="center"
        style="min-height: 48px"
      >
        <v-col
          v-if="!hasNoPlayers"
          cols="auto"
          class="mr-2"
        >
          <players-autocomplete
            key="eventPlayersFilter"
            v-model="filters.playerId"
            :players="playersByTeam"
            :loading="players.loading"
            solo
            :chips="false"
            clearable
            hide-details
            :placeholder="$t('text_input.search_by_player.title')"
            style="width: 300px"
          />
        </v-col>
        <v-col
          cols="auto"
          class="mr-2"
        >
          <v-select
            v-model="filters.coachIds"
            :items="coachesByOrganisation"
            item-text="full_name"
            item-value="id"
            multiple
            solo
            clearable
            hide-details
            :placeholder="$t('dropdown.coaches.placeholder')"
            style="width: 300px"
            :loading="coaches.loading"
          >
            <template v-slot:selection="{ item, index }">
              <v-chip v-if="index === 0">
                <span>{{ item.full_name }}</span>
              </v-chip>
              <span
                v-if="index === 1"
                class="grey--text text-caption"
              >
                (+{{ filters.coachIds.length - 1 }} {{ $t('dropdown.others.label') }})
              </span>
            </template>
          </v-select>
        </v-col>
        <template v-if="activeTags.length > 0 && activeTags.length <= 2">
          <v-col
            v-for="tag in activeTags"
            :key="'tag'+tag.id"
            cols="auto"
            class="mr-2"
          >
            <v-select
              v-model="filters.tags[tag.id]"
              :items="tag.items"
              :label="$t(`training_exercise_tags.labels.${toCamelCase(tag.id)}`)"
              solo
              style="width: 200px"
              background-color="white"
              clearable
              hide-details
              :multiple="tag.multiple_choice"
              @change="tagChanged(tag.id)"
            />
          </v-col>
        </template>
        <template v-else-if="activeTags.length > 2">
          <v-col
            cols="auto"
            class="mr-2"
          >
            <v-btn
              color="primary"
              text
              class="text-none"
              @click="filtersDialog = true"
            >
              {{ $t('button.filters') }}
              <v-avatar
                v-if="activeFiltersCount > 0"
                size="20"
                color="primary"
                class="ml-2 white--text"
                style="font-size: 12px"
              >
                {{ activeFiltersCount }}
              </v-avatar>
            </v-btn>
          </v-col>
        </template>
        <v-spacer />
        <v-col
          cols="auto"
          class="pt-1"
        >
          <v-btn
            color="primary"
            text
            class="text-none"
            @click="openDrill = true; selectedDrill = null"
          >
            <v-icon
              x-small
              left
            >
              far fa-plus
            </v-icon>
            {{ $t('button.create_new_drill') }}
          </v-btn>
        </v-col>
      </v-row>
    </div>
    <v-divider class="mt-2" />
    <div style="height: 1px">
      <v-progress-linear
        v-if="drills.loading || submitting || deleting"
        indeterminate
        color="primary"
        :height="1"
      />
    </div>

    <v-row
      no-gutters
      align="center"
      style="min-height: 48px"
    >
      <v-col cols="auto">
        <v-btn-toggle
          v-model="filters.shareStatus"
          mandatory
          color="primary"
          dense
          class="rounded-lg mr-4"
        >
          <v-btn
            value="methodology"
            class="text-none"
            small
          >
            {{ $t('training_builder.filter.methodology_department') }}
          </v-btn>
          <v-btn
            value="custom"
            class="text-none"
            small
          >
            {{ $t('training_builder.filter.custom') }}
          </v-btn>
        </v-btn-toggle>
      </v-col>
      <v-col
        v-if="$can('create-shared-drills')"
        cols="auto"
      >
        <v-switch
          v-model="filters.onlyMyDrills"
          dense
          :label="$t('training_builder.switch.only_my_drills')"
          class="pa-0 ma-0"
          hide-details
        />
      </v-col>
      <v-spacer />
      <v-col cols="auto">
        <div
          v-if="drills.pagination.last_page > 1"
          class="pt-1"
        >
          <pagination
            v-model="drills.pagination.current_page"
            :last-page="drills.pagination.last_page"
          />
        </div>
      </v-col>
    </v-row>

    <v-row
      v-if="drills.data.length > 0"
      class="mt-2"
    >
      <drill-card
        v-for="drill in drills.data"
        :key="drill.id"
        :drill="drill"
        @click="handleDrillCardClick(drill)"
      />
    </v-row>

    <v-row
      v-else-if="!drills.loading"
      class="mt-5"
      justify="center"
    >
      <v-col cols="auto">
        {{ noFilters ? $t("drill.no_drills_created") : $t("drill.no_drills_match_filter") }}
      </v-col>
    </v-row>

    <card-dialog
      :key="'Create new' + createNewDrillKey"
      v-model="openDrill"
      :title="$t('card_title.drill.create_new')"
      :scrollable="false"
      fullscreen
      :prevent-close="true"
      @close="handleClose"
    >
      <v-card-text class="pa-0">
        <div
          style="height: 2px"
        >
          <v-progress-linear
            v-if="submitting"
            indeterminate
            color="primary"
            :height="2"
          />
        </div>
        <HomeView
          :teams="teams"
          :players="players.data"
          :sport="currentTeam.sport.name"
          :user="user"
          :tags="cloneDeep(tags)"
          :any-progress-made.sync="hasUnsavedChanges"
          @save="handleSave"
          @saveExercise="handleSaveExercise"
          @close="handleClose"
          @update-organisation-setting="handleOrganisationSettingUpdate"
        />
      </v-card-text>
    </card-dialog>

    <card-dialog
      v-if="selectedDrill"
      :key="'Edit' + editDrillKey"
      v-model="editDrill"
      :title="$t('card_title.drill.edit')"
      :scrollable="false"
      fullscreen
      :prevent-close="true"
      @close="handleClose"
    >
      <v-card-text class="pa-0">
        <div
          style="height: 2px"
        >
          <v-progress-linear
            v-if="submitting"
            indeterminate
            color="primary"
            :height="2"
          />
        </div>
        <HomeView
          :drill="selectedDrill"
          :teams="teams"
          :players="players.data"
          :sport="currentTeam.sport.name"
          :tags="cloneDeep(tags)"
          :user="user"
          :open-export="openExport"
          :any-progress-made.sync="hasUnsavedChanges"
          @save="handleSave"
          @update-organisation-setting="handleOrganisationSettingUpdate"
          @close="handleClose"
        />
      </v-card-text>
    </card-dialog>

    <card-dialog
      v-if="selectedDrill"
      v-model="drillInfoModal"
      :scrollable="false"
      max-width="1200px"
      @close="selectedDrill = null"
    >
      <template v-slot:title>
        <v-card-title
          v-if="!$slots.title"
          class="blue-grey lighten-5 px-6 py-4"
        >
          <div>
            <div>
              {{ $t('card_title.drill.info') }}
            </div>
            <div
              v-if="selectedDrill && selectedDrill.coach"
              class="text-body-2 mt-2 grey--text"
            >
              {{ $t('player_note.created_by', { name: selectedDrill.coach.first_name + ' ' + selectedDrill.coach.last_name, timestamp: $dayjs(selectedDrill.created_at).format(getDateTimeFormatForUser('ddd, MMM D HH:mm:ss')) }) }}
            </div>
          </div>
        </v-card-title>
      </template>
      <v-card-text>
        <v-row
          class="mt-2"
        >
          <v-col
            cols="8"
            align-self="center"
          >
            <v-img
              v-if="selectedDrill.preview_url"
              id="drill_preview"
              class="rounded-xl fa-border"
              :src="selectedDrill.preview_url"
              @contextmenu.prevent="onContextMenu"
            />
            <v-img
              v-else
              height="450px"
              class="rounded-xl fa-border"
            >
              <template v-slot:placeholder>
                <v-row
                  no-gutters
                  align="center"
                  justify="center"
                  class="fill-height"
                >
                  <v-col cols="auto">
                    <v-icon x-large>
                      fal fa-court-sport
                    </v-icon>
                  </v-col>
                </v-row>
              </template>
            </v-img>
          </v-col>
          <v-col>
            <v-row
              class="flex-column"
              style="height: 100%;"
            >
              <v-col cols="auto">
                <v-row no-gutters>
                  <v-col>
                    <span class="text--black text-h6">
                      {{ selectedDrill.name }}
                    </span>
                  </v-col>
                  <v-col
                    v-if="isSelectedDrillEditable"
                    cols="auto"
                  >
                    <v-btn
                      text
                      small
                      :loading="loading"
                      color="primary"
                      class="text-none"
                      @click="openEditDrillDialog()"
                    >
                      <v-icon
                        left
                        small
                      >
                        far fa-pen
                      </v-icon>
                      {{ $t("button.edit") }}
                    </v-btn>
                  </v-col>
                </v-row>
              </v-col>
              <v-col cols="auto">
                <span class="text--black text-subtitle-1">
                  {{ selectedDrill.description }}
                </span>
              </v-col>
              <v-spacer />
              <v-col
                v-if="isSelectedDrillEditable"
                cols="auto"
                class="pt-0"
              >
                <v-btn
                  text
                  :loading="loading"
                  color="red"
                  class="text-none"
                  outlined
                  width="100%"
                  @click="deleteConfirmationDialogVisible = true"
                >
                  <v-icon
                    left
                    x-small
                  >
                    fa fa-trash
                  </v-icon>
                  {{ $t('button.delete') }}
                </v-btn>
              </v-col>
              <v-col
                cols="auto"
                class="pt-0"
              >
                <v-btn
                  text
                  :loading="loading"
                  color="primary"
                  class="text-none"
                  outlined
                  width="100%"
                  @click="duplicate()"
                >
                  <v-icon
                    left
                    x-small
                  >
                    fa fa-copy
                  </v-icon>
                  {{ $t("button.duplicate") }}
                </v-btn>
              </v-col>
              <v-col
                v-if="isSelectedDrillEditable"
                cols="auto"
                class="py-0"
              >
                <v-btn
                  :loading="loading"
                  color="primary"
                  class="text-none"
                  width="100%"
                  @click="openEditDrillDialog(true)"
                >
                  <v-icon
                    left
                    x-small
                  >
                    fa fa-file-export
                  </v-icon>
                  {{ $t("button.export") }}
                </v-btn>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-card-text>
    </card-dialog>

    <training-exercise-filter-dialog
      :visible.sync="filtersDialog"
      :tags="activeTags"
      :filters.sync="filters.tags"
      @click:apply="fetchDrillsWithoutPageChange()"
    />

    <delete-confirmation-dialog
      :visible.sync="deleteConfirmationDialogVisible"
      @confirmed="deleteDrill()"
    />

    <delete-confirmation-dialog
      :visible.sync="closeConfirmationDialogVisible"
      :title="$t('card_dialog.lose_progress.title')"
      :text="$t('card_dialog.lose_progress.label')"
      :cta-text="$t('card_dialog.lose_progress.button')"
      @confirmed="confirmClose"
    />
  </div>
</template>

<script>
import ViewPageHeader from '@/components/ViewPageHeader'
import DrillCard from '@/components/drill/DrillCard.vue'
import { DrillApiService } from '@/data/services/DrillApiService'
import Pagination from '@/components/Pagination.vue'
import CardDialog from '@/components/helpers/CardDialog.vue'
import { EventBus } from '@/plugins/bus'
import { AuthApiService, CoachApiService, PlayerApiService } from '@/data/services'
import PlayersAutocomplete from '@/components/helpers/PlayersAutocomplete.vue'
import { TrainingExerciseApiService } from '@/data/services/TrainingExerciseApiService'
import DeleteConfirmationDialog from '@/components/dialogs/DeleteConfirmationDialog.vue'
import { RouteNames } from '@/router/routes'
import { getDateTimeFormatForUser, toCamelCase, getTranslatedTagValue } from '@/utils/helpers'
import { OrganisationApiService } from '@/data/services/OrganisationApiService'
import { cloneDeep } from 'lodash'
import TrainingExerciseFilterDialog from '@/components/dialogs/TrainingExerciseFilterDialog.vue'

export default {
  name: 'DrillListView',
  components: {
    DeleteConfirmationDialog,
    PlayersAutocomplete,
    CardDialog,
    Pagination,
    DrillCard,
    ViewPageHeader,
    HomeView: () => import('@ultrax-ai/drill-package'),
    TrainingExerciseFilterDialog
  },
  props: {
    attachMode: {
      type: Boolean,
      default: false
    }
  },
  data: () => ({
    drills: {
      data: [],
      loading: false,
      pagination: {
        current_page: 1,
        last_page: undefined,
        per_page: 10
      }
    },
    players: {
      data: [],
      loading: false
    },
    coaches: {
      data: [],
      loading: false
    },
    filters: {
      playerId: null,
      coachIds: [],
      tags: {},
      shareStatus: undefined,
      onlyMyDrills: false
    },
    groups: {
      loading: false,
      data: []
    },
    openDrill: false,
    editDrill: false,
    loading: false,
    submitting: false,
    deleting: false,
    drillInfoModal: false,
    selectedDrill: null,
    playersForDrill: null,
    deleteConfirmationDialogVisible: false,
    closeConfirmationDialogVisible: false,
    createNewDrillKey: 0,
    editDrillKey: 0,
    openExport: false,
    hasUnsavedChanges: false,
    filtersDialog: false
  }),
  computed: {
    user() {
      return this.$store.getters[this.$storeGetters.AUTH_USER]
    },
    teams() {
      return this.$store.getters[this.$storeGetters.APP_TEAMS]
    },
    currentTeam() {
      return this.$store.getters[this.$storeGetters.APP_CURRENT_TEAM]
    },
    playersByTeam() {
      return this.players.data.filter(p => p.team_id === this.currentTeam.id)
    },
    coachesByOrganisation() {
      return this.coaches.data.map(c => ({
        ...c,
        full_name: `${c.first_name} ${c.last_name}`
      }))
    },
    selectedGroup() {
      const group = this.groups.data.find(g => g.sport_id === this.currentTeam.sport.id)

      if (!group) return undefined

      group.params.tags = group.params.tags.map(tag => {
        let rawItems = []
        if (tag.filter_by) {
          if (this.filters.tags[tag.filter_by]) rawItems = tag.values[this.filters.tags[tag.filter_by]] || []
          else rawItems = []
        } else {
          rawItems = tag.values || []
        }
        tag.items = rawItems.map(value => ({
          text: getTranslatedTagValue(value),
          value: value
        }))

        return tag
      })

      return group
    },
    tags() {
      return this.selectedGroup?.params?.tags || []
    },
    activeTags() {
      return this.tags.filter(t => !t.is_hidden) || []
    },
    activeFiltersCount() {
      return Object.values(this.filters.tags).filter(v => v !== undefined && v !== null).length
    },
    noFilters() {
      const defaultShareStatus = this.$can('create-shared-drills') ? 'methodology' : 'custom'
      return this.filters.playerId === null && Object.keys(this.filters.tags).length === 0 && this.filters.shareStatus === defaultShareStatus && !this.filters.onlyMyDrills
    },
    hasNoPlayers() {
      return this.user.organisation_settings?.has_no_players || false
    },
    teamWithDrill() {
      return ['Football', 'Futsal', 'Handball', 'Basketball', 'Hockey', 'US Football', 'Water Polo', 'Volleyball', 'Badminton', 'Baseball', 'Lacrosse', 'Pickleball', 'Tennis', 'Field Hockey'].includes(this.currentTeam?.sport?.name) || false
    },
    isSelectedDrillEditable() {
      return this.selectedDrill && (this.$can('create-shared-drills') || (this.selectedDrill.coach && this.selectedDrill.coach.id === this.user.coach.id))
    }
  },
  watch: {
    'drills.pagination.current_page': function (newVal, oldVal) {
      this.fetchDrills()
    },
    currentTeam: {
      handler() {
        if (!this.teamWithDrill) {
          this.$router.push({ name: RouteNames.DASHBOARD })
        }

        this.filters.playerId = null
        this.fetchDrillsWithoutPageChange()
      }
    },
    'filters.playerId': function () {
      this.fetchDrillsWithoutPageChange()
    },
    'filters.coachIds': function () {
      this.fetchDrillsWithoutPageChange()
    },
    'filters.shareStatus': function () {
      this.fetchDrillsWithoutPageChange()
    },
    'filters.onlyMyDrills': function () {
      this.fetchDrillsWithoutPageChange()
    },
    openDrill: function () {
      this.createNewDrillKey++
    },
    editDrill: function (newVal) {
      this.editDrillKey++
      if (!newVal) {
        this.openExport = false
      }
    }
  },
  created() {
    this.filters.shareStatus = this.$can('create-shared-drills') ? 'methodology' : 'custom'
    this.fetchDrills()
    this.fetchPlayers()
    this.fetchCoaches()
    this.fetchAllGroups()

    EventBus.$on(this.$events.DRILL_CREATED, () => this.fetchDrillsWithoutPageChange())
    EventBus.$on(this.$events.DRILL_UPDATED, () => this.fetchDrillsWithoutPageChange())
    EventBus.$on(this.$events.DRILL_DELETED, () => this.fetchDrillsWithoutPageChange())

    document.addEventListener('keydown', this.handleKeyboard.bind(this))
  },
  destroyed() {
    EventBus.$off(this.$events.DRILL_CREATED)
    EventBus.$off(this.$events.DRILL_UPDATED)
    EventBus.$off(this.$events.DRILL_DELETED)

    document.removeEventListener('keydown', this.handleKeyboard.bind(this))
  },
  methods: {
    cloneDeep,
    getDateTimeFormatForUser,
    toCamelCase,
    handleClose() {
      if (this.hasUnsavedChanges) {
        this.closeConfirmationDialogVisible = true
      } else {
        this.openDrill = false
        this.editDrill = false
      }
    },
    confirmClose() {
      this.closeConfirmationDialogVisible = false
      this.openDrill = false
      this.editDrill = false
      this.hasUnsavedChanges = false
    },
    async fetchDrills() {
      try {
        this.drills.loading = true

        const filteredTags = Object.fromEntries(
          Object.entries(this.filters.tags).filter(([_, v]) => v != null)
        )

        const queryParams = {
          page: this.drills.pagination.current_page,
          per_page: this.drills.pagination.per_page,
          team_id: this.currentTeam.id,
          player_id: this.filters.playerId || undefined,
          coach_ids: this.filters.coachIds.length > 0 ? this.filters.coachIds : undefined,
          is_shared: this.filters.shareStatus === 'methodology',
          only_my_drills: this.filters.onlyMyDrills || undefined,
          tags: filteredTags
        }

        const result = await DrillApiService.getPaginated(queryParams)

        this.drills.data = result.data
        this.drills.pagination = result.meta
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.drill_loading.error'),
          color: 'error'
        })

        throw e
      } finally {
        this.drills.loading = false
      }
    },
    fetchDrillsWithoutPageChange() {
      if (this.drills.pagination.current_page === 1) {
        this.fetchDrills()
      } else {
        this.drills.pagination.current_page = 1
      }
    },
    async handleSave(saveData) {
      if (saveData.state) {
        await this.save(saveData)
      }
    },
    async handleSaveExercise(saveData) {
      try {
        this.submitting = true

        const drillData = {
          name: saveData.name,
          description: saveData.description,
          preview: saveData.preview,
          state: saveData.state,
          coach_id: this.user.coach.id,
          team_ids: saveData.team_ids,
          player_ids: saveData.player_ids,
          is_hidden: true,
          tags: saveData.tags
        }

        const result = await DrillApiService.create(drillData)

        const exerciseData = {
          name: saveData.name,
          description: saveData.description,
          training_exercise_group_id: this.selectedGroup.id,
          tags: saveData.tags,
          media_ids: [],
          drill_id: result.data.id,
          is_shared: saveData.is_shared
        }

        await TrainingExerciseApiService.createUserExercise(exerciseData)

        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.exercise_save.success'),
          color: 'success'
        })

        this.openDrill = false

        EventBus.$emit(this.$events.TRAINING_EXERCISE_CREATED, result.data)
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.exercise_save.error'),
          color: 'error'
        })
        throw e
      } finally {
        this.submitting = false
      }
    },
    async save(saveData) {
      try {
        this.submitting = true

        const data = {
          name: saveData.name,
          description: saveData.description,
          preview: saveData.preview,
          state: saveData.state,
          coach_id: this.user.coach.id,
          team_ids: saveData.team_ids,
          player_ids: saveData.player_ids,
          tags: saveData.tags,
          is_shared: saveData.is_shared
        }

        if (this.selectedDrill) {
          await DrillApiService.update(this.selectedDrill.id, data)

          this.$store.dispatch(this.$storeActions.ALERT, {
            text: this.$t('alert.drill_update.success'),
            color: 'success'
          })

          EventBus.$emit(this.$events.DRILL_UPDATED)
        } else {
          const response = await DrillApiService.create(data)

          this.selectedDrill = response.data

          this.$store.dispatch(this.$storeActions.ALERT, {
            text: this.$t('alert.drill_save.success'),
            color: 'success'
          })

          EventBus.$emit(this.$events.DRILL_CREATED)
        }
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.drill_save.error'),
          color: 'error'
        })
        throw e
      } finally {
        this.submitting = false
      }
    },
    async deleteDrill() {
      try {
        this.deleting = true
        this.deleteConfirmationDialogVisible = false

        await DrillApiService.delete(this.selectedDrill.id)

        this.drillInfoModal = false
        this.selectedDrill = null

        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.drill_delete.success'),
          color: 'success'
        })

        EventBus.$emit(this.$events.DRILL_DELETED)
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.drill_delete.error'),
          color: 'error'
        })
        throw e
      } finally {
        this.deleting = false
      }
    },
    async fetchPlayers() {
      try {
        this.players.loading = true

        const resp = await PlayerApiService.getSimpleList({})

        this.players.data = resp.data
      } catch (e) {
        this.players.data = []
      } finally {
        this.players.loading = false
      }
    },
    async fetchCoaches() {
      try {
        this.coaches.loading = true

        const result = await CoachApiService.getPaginated({
          order_by: 'last_name',
          order: 'ASC'
        })

        this.coaches.data = result.data
      } catch (e) {
        this.coaches.data = []
      } finally {
        this.coaches.loading = false
      }
    },
    async fetchAllGroups() {
      this.groups.loading = true

      try {
        const result = await TrainingExerciseApiService.getAllGroups()

        this.groups.data = result.map(group => {
          return {
            ...group,
            name: this.$t(`training_exercise_groups.${toCamelCase(group.name)}`)
          }
        })
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.loading_groups.error'),
          color: 'error'
        })

        throw e
      } finally {
        this.groups.loading = false
      }
    },
    tagChanged(tagId, fetchDrills = true) {
      const tagDefinitions = this.selectedGroup?.params?.tags

      if (!tagDefinitions) return

      const dependingTags = tagDefinitions.filter(tag => tag.filter_by === tagId)

      for (const tag of dependingTags) {
        if (this.filters.tags[tag.id]) {
          this.filters.tags[tag.id] = undefined
          this.tagChanged(tag.id, false)
        }
      }

      if (fetchDrills) this.fetchDrillsWithoutPageChange()
    },
    async duplicate() {
      try {
        this.loading = true

        const previewResponse = await fetch(this.selectedDrill.preview_url)
        const previewBlob = await previewResponse.blob()
        const previewData = await new Promise((resolve) => {
          const reader = new FileReader()
          reader.onload = () => resolve(reader.result)
          reader.readAsDataURL(previewBlob)
        })

        const stateResponse = await fetch(this.selectedDrill.state_url)
        const stateData = await stateResponse.json()

        const saveData = {
          name: this.selectedDrill.name + ' copy',
          description: this.selectedDrill.description,
          preview: previewData,
          state: stateData,
          coach_id: this.user.coach.id,
          team_ids: this.selectedDrill.team_ids,
          player_ids: this.selectedDrill.player_ids,
          tags: this.selectedDrill.tags.reduce((acc, tag) => {
            acc[tag.tag_id] = tag.value
            return acc
          }, {}),
          is_shared: this.selectedDrill.is_shared
        }

        this.selectedDrill = null

        await this.save(saveData)
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.drill_duplicate.error'),
          color: 'error'
        })
        throw e
      } finally {
        this.loading = false
      }
    },
    handleKeyboard(event) {
      event.stopPropagation()
      if (event.keyCode === 27) {
        if (this.openDrill || this.editDrill) this.closeConfirmationDialogVisible = true
      }
    },
    async openEditDrillDialog(isExport = false) {
      try {
        this.loading = true
        const stateResponse = await fetch(this.selectedDrill.state_url)
        const state = await stateResponse.json()

        this.selectedDrill = {
          ...this.selectedDrill,
          state: state
        }

        this.openExport = isExport
        this.drillInfoModal = false
        this.editDrill = true
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.drill_loading.error'),
          color: 'error'
        })
      } finally {
        this.loading = false
      }
    },
    async handleOrganisationSettingUpdate({ key, value }) {
      try {
        const data = {}
        data[key] = value

        await OrganisationApiService.updateDrillSettings(data)

        const authResponse = await AuthApiService.me()

        this.$store.dispatch(this.$storeActions.AUTH_LOGIN, authResponse.data)
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.organisation_settings_update.error'),
          color: 'error'
        })
        throw e
      }
    },
    handleDrillCardClick(drill) {
      if (this.attachMode) {
        this.$emit('attach', drill)
      } else {
        this.selectedDrill = drill
        this.drillInfoModal = true
      }
    },
    onContextMenu(e) {
      if (!this.$can('create-shared-drills')) {
        e.preventDefault()
        return
      }

      return true
    }
  }

}
</script>
